import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Divider,
  Alert,
  useTheme,
  Slide,
  Autocomplete,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useQuery } from "@apollo/client";
import { GET_CUSTOMER_INTEGRATIONS } from "../../../../apis/settingsApis/integration-apis/integrationApis";
import AWSGuide from "../../../Organization/InfraManagement/InfraTemplateDialog/guides/AWSGuide";
import awsLogo from "../../../../Assets/Images/aws.png";
import { AWS_REGIONS } from "../../../../utils/enums";

const AWSConnectionDrawer = ({
  open,
  onClose,
  refetchIntegrationDetails,
  onStoreIntegrationDetails,
}) => {
  const theme = useTheme();
  const [isIntegrationActive, setIsIntegrationActive] = useState(undefined);
  const [integrationInactivityReason, setIntegrationInactivityReason] =
    useState(undefined);
  const [awsArn, setAwsArn] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Empty means "scan every region", which is what happens today.
  const [regions, setRegions] = useState([]);

  const {
    data: customerIntegrationsResponse,
    loading: loadingCustomerIntegrations,
    refetch: refetchCustomerIntegrations,
  } = useQuery(GET_CUSTOMER_INTEGRATIONS);

  useEffect(() => {
    if (customerIntegrationsResponse) {
      const integration =
        customerIntegrationsResponse?.getAccountDetails?.userIntegrationsDetails?.list?.find(
          (item) => item.integration_key === "aws"
        );
      if (!integration) {
        setIsIntegrationActive(false);
      } else if (integration?.is_active) {
        setIsIntegrationActive(true);
      } else if (!integration?.is_active && integration?.inactivity_reason) {
        setIsIntegrationActive(false);
        // setIntegrationInactivityReason(integration?.inactivity_reason);
      }
    }
  }, [customerIntegrationsResponse]);

  const slideTransition = useMemo(
    () => (props) => <Slide {...props} direction="left" />,
    []
  );

  const handleSubmit = async () => {
    const integrationResourceId = awsArn.trim();

    if (!onStoreIntegrationDetails) {
      console.error("onStoreIntegrationDetails is not provided");
      return;
    }

    setSubmitting(true);
    try {
      const result = await onStoreIntegrationDetails({
        integratedAccountId: integrationResourceId,
        integrationKey: "aws",
        integrationType: "AWS",
        sync: true,
        regions: regions.map((r) => r.value),
      });

      if (result?.success) {
        onClose();
      }
    } catch (error) {
      console.error("Error updating integration details:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      TransitionComponent={slideTransition}
      PaperProps={{
        sx: {
          width: "75%",
          maxWidth: "1400px",
          backgroundColor: theme.customColors.background.main,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#FFFFFF",
          borderBottom: `1px solid ${theme.customColors.border.subtle}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 4,
            py: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "10px",
                backgroundColor: theme.customColors.background.cardHeader,
                border: `1px solid ${theme.customColors.border.main}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={awsLogo}
                alt="AWS"
                style={{ width: 32, height: 32, objectFit: "contain" }}
              />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors.text.strong,
                  letterSpacing: "-0.02em",
                }}
              >
                Connect Amazon Web Services
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  mt: 0.5,
                }}
              >
                Follow the steps below to establish a secure connection
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ color: theme.customColors.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 4,
          py: 3,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.customColors.border.main,
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: theme.customColors.border.strong,
          },
        }}
      >

        {/* Setup Guide Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: theme.customColors.text.strong,
            }}
          >
            Setup Guide
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors.text.secondary,
              mb: 3,
            }}
          >
            Complete the following steps in your AWS account before entering
            your credentials
          </Typography>
          <AWSGuide />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Credentials Form Section */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: theme.customColors.text.strong,
            }}
          >
            Provide Your Credentials
          </Typography>
          <TextField
            label="AWS Role ARN"
            value={awsArn}
            onChange={(e) => setAwsArn(e.target.value)}
            fullWidth
            placeholder="arn:aws:iam::123456789012:role/RegoditScannerRole"
            helperText={
              integrationInactivityReason
                ? "Existing ARN found though seems to have a problem. You can update it if needed."
                : "Enter the ARN from your CloudFormation stack outputs."
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={AWS_REGIONS}
            value={regions}
            onChange={(event, value) => setRegions(value)}
            getOptionLabel={(option) => `${option.label} — ${option.value}`}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.value}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Regions to scan"
                placeholder={regions.length ? "" : "All regions"}
                helperText="Leave empty to scan every region. Resources outside your selection will not be monitored."
              />
            )}
            sx={{
              mt: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#FFFFFF",
          borderTop: `1px solid ${theme.customColors.border.subtle}`,
          px: 4,
          py: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            color: theme.customColors.text.primary,
            fontWeight: 500,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!awsArn.trim() || loadingCustomerIntegrations || submitting}
          startIcon={<CheckCircleIcon />}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "white",
            textTransform: "none",
            px: 4,
            py: 1.5,
            fontWeight: 600,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: theme.customColors.button.hover,
            },
            "&:disabled": {
              backgroundColor: theme.customColors.background.disabled,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          {submitting ? "Connecting..." : "Connect Integration"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default AWSConnectionDrawer;
